import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import type { UserStatsResponse } from "@/domain/gems-game/domain/lib/Sweepstake";
import { formatDateOnly } from "@/utils/date";
import { pluralize } from "@/utils/string";
import { onDarkBase } from "@biom3/design-tokens";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { Card, Link, List, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { QuestCompletionCard } from "../KeyInfo/QuestCompletionCard";
import { RarityText, RarityTextWithIndefiniteArticle } from "../RarityText";

interface ProgressBarDropdownContentProps {
  userStats?: UserStatsResponse;
  rarity: Rarity;
  onClose: () => void;
}

const getQuestStats = (userStats: UserStatsResponse | undefined) => {
  const completed = new Map<
    string,
    { timesCompleted: number; totalPointsEarned: number }
  >();

  const incomplete = new Map<
    string,
    { timesCompleted: number; totalPointsEarned: number }
  >();

  const now = new Date();

  for (const quest of userStats?.targetQuests ?? []) {
    const targetMap =
      (quest.next_available_at && new Date(quest.next_available_at) <= now) ||
      !quest.completed
        ? incomplete
        : completed;

    if (targetMap) {
      const stats = targetMap.get(quest.external_id);

      if (stats) {
        stats.timesCompleted++;
        stats.totalPointsEarned += quest.points;
      } else {
        targetMap.set(quest.external_id, {
          timesCompleted: 1,
          totalPointsEarned: quest.points,
        });
      }
    }
  }

  return { completed, incomplete };
};

const getTotalGamesPlayed = (
  userStats: UserStatsResponse | undefined,
): number => {
  const games: Set<string> = new Set();
  for (const quest of userStats?.targetQuests.filter((q) => q.completed) ??
    []) {
    games.add(quest.game_id);
  }
  return games.size;
};

const getTotalQuestsCompleted = (
  userStats: UserStatsResponse | undefined,
): number => {
  return (
    userStats?.targetQuests
      ?.filter((q) => q.completed)
      .reduce((acc) => acc + 1, 0) ?? 0
  );
};

export function ProgressBarDropdownContent({
  userStats,
  rarity,
  onClose,
}: ProgressBarDropdownContentProps) {
  const [showAllIncomplete, setShowAllIncomplete] = useState(false);

  const questStats = useMemo(() => {
    return getQuestStats(userStats);
  }, [userStats]);

  const completedQuestStats = questStats.completed;
  const incompleteQuestStats = questStats.incomplete;

  const incompleteQuestEntries = Array.from(incompleteQuestStats.entries());
  const displayedIncompleteQuests = !showAllIncomplete
    ? incompleteQuestEntries.slice(0, 5)
    : incompleteQuestEntries;
  const hasMoreIncompleteQuests = incompleteQuestEntries.length > 5;

  const totalQuestsCompleted = useMemo(() => {
    return getTotalQuestsCompleted(userStats);
  }, [userStats]);

  const totalGamesPlayed = useMemo(() => {
    return getTotalGamesPlayed(userStats);
  }, [userStats]);

  const completionMessage = useMemo(() => {
    const questCompletionText =
      totalQuestsCompleted === 0 && totalGamesPlayed === 0 ? (
        "You have completed no eligible quests"
      ) : (
        <>
          You have completed <strong>{totalQuestsCompleted}</strong> eligible{" "}
          {pluralize(totalQuestsCompleted, "quest", "quests")} across{" "}
          <strong>{totalGamesPlayed}</strong>{" "}
          {pluralize(totalGamesPlayed, "game", "games")}
        </>
      );

    return (
      <>
        {questCompletionText} so far. If the week ended today you would earn{" "}
        <strong>
          <RarityTextWithIndefiniteArticle rarity={rarity} /> Key
        </strong>
        .
      </>
    );
  }, [totalQuestsCompleted, totalGamesPlayed, rarity]);

  return (
    <Stack spacing={3}>
      <Card
        variant="outlined"
        sx={{ backgroundColor: onDarkBase.color.neutral[700], p: 2 }}
      >
        <Typography variant="h6">
          Next Draw ({userStats?.sweepstake_name}) – Projected Key:{" "}
          <RarityText rarity={rarity} sx={{ textTransform: "uppercase" }} />
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Claimable:{" "}
          {userStats?.completionPeriodStartDate &&
            formatDateOnly(new Date(userStats.completionPeriodStartDate))}{" "}
          –{" "}
          {userStats?.completionPeriodEndDate &&
            formatDateOnly(new Date(userStats.completionPeriodEndDate))}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {completionMessage}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            display: "block",
            textTransform: "none",
            color: onDarkBase.color.text.caption.secondary,
          }}
        >
          Only quests you complete between{" "}
          <strong>
            {userStats?.completionPeriodStartDate &&
              formatDateOnly(new Date(userStats.completionPeriodStartDate))}
          </strong>{" "}
          and{" "}
          <strong>
            {userStats?.completionPeriodEndDate &&
              formatDateOnly(new Date(userStats.completionPeriodEndDate))}
          </strong>{" "}
          count toward your next Key. Your projected tier updates in real time,
          and your final Key may differ from the projection.
        </Typography>
      </Card>

      {incompleteQuestStats.size > 0 && (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalFireDepartmentIcon
              sx={{ fontSize: 22, color: onDarkBase.color.status.fatal.bright }}
            />
            <Typography variant="body1">
              Complete{" "}
              <Link
                href="/quests/#main-quests"
                onClick={onClose}
                style={{
                  textDecoration: "none",
                  color: onDarkBase.color.accent[4],
                }}
              >
                more eligible quests
              </Link>{" "}
              now for a better Key!
            </Typography>
          </Stack>
          <List disablePadding sx={{ width: "100%", mt: 0 }}>
            {displayedIncompleteQuests.map(
              ([questId, questStats]: [
                string,
                { timesCompleted: number; totalPointsEarned: number },
              ]) => {
                const quest = userStats?.targetQuests?.find(
                  (q) => q.external_id === questId,
                );
                return (
                  <QuestCompletionCard
                    key={questId}
                    name={quest?.name || quest?.external_id || ""}
                    gameName={quest?.game_name || ""}
                    timesCompleted={questStats.timesCompleted}
                    totalPointsEarned={questStats.totalPointsEarned}
                    dimmed={true}
                  />
                );
              },
            )}
          </List>
          {hasMoreIncompleteQuests && !showAllIncomplete && (
            <Typography
              variant="body2"
              sx={{
                color: onDarkBase.color.accent[4],
                cursor: "pointer",
                textAlign: "center",
                mt: 1,
              }}
              onClick={() => setShowAllIncomplete(true)}
            >
              View all {incompleteQuestEntries.length} quests
            </Typography>
          )}
        </Stack>
      )}

      {completedQuestStats.size > 0 && (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon sx={{ fontSize: 22 }} color="success" />
            <Typography variant="h6" component="div">
              Completed this week
            </Typography>
          </Stack>
          <List disablePadding sx={{ width: "100%", mt: 0 }}>
            {Array.from(completedQuestStats.entries()).map(
              ([questId, questStats]: [
                string,
                { timesCompleted: number; totalPointsEarned: number },
              ]) => {
                const quest = userStats?.targetQuests?.find(
                  (q) => q.external_id === questId,
                );
                return (
                  <QuestCompletionCard
                    key={questId}
                    name={quest?.name || quest?.external_id || ""}
                    gameName={quest?.game_name || ""}
                    timesCompleted={questStats.timesCompleted}
                    totalPointsEarned={questStats.totalPointsEarned}
                  />
                );
              },
            )}
          </List>
        </Stack>
      )}
    </Stack>
  );
}
